import { useCallback, useMemo, useState } from "react";

import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { EntityTypeEnum } from "@/features/outlet-chart-of-account-transactions";
import { formatNumber } from "@/features/shared";

import { AccountTransactionFormDialog } from "../account-transaction-form-dialog";

const getEntityLabel = (transaction) => {
  if (!transaction.entityType || !transaction.entity) return null;
  let typeLabel;
  switch (transaction.entityType) {
    case EntityTypeEnum.SUPPLIER:
      typeLabel = "Pemasok";
      break;
    case EntityTypeEnum.CUSTOMER:
      typeLabel = "Pelanggan";
      break;
    case EntityTypeEnum.OUTLET:
      typeLabel =
        transaction.entity.role === "HUB" ? "Outlet Hub" : "Outlet Retail";
      break;
    default:
      typeLabel = "";
  }
  return `${typeLabel}: ${transaction.entity.name}`;
};

export const AccountTransactionsSection = () => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const outlet = useWatch({ control, name: "outlet" });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "outletChartOfAccountTransactions",
  });

  const transactions = useWatch({
    control,
    name: "outletChartOfAccountTransactions",
  });

  const totals = useMemo(() => {
    if (!transactions) return { debit: 0, credit: 0, difference: 0 };

    const debit = transactions.reduce(
      (sum, t) => sum + (Number(t?.debit) || 0),
      0,
    );
    const credit = transactions.reduce(
      (sum, t) => sum + (Number(t?.credit) || 0),
      0,
    );

    return { debit, credit, difference: debit - credit };
  }, [transactions]);

  const isBalanced = Math.abs(totals.difference) < 0.01;

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [dialogDefaults, setDialogDefaults] = useState(null);

  const handleOpenAddDialog = () => {
    setEditIndex(null);

    // Smart suggestion: pre-fill with balancing amount if there's an imbalance
    if (!isBalanced && Math.abs(totals.difference) > 0) {
      const suggestedType = totals.difference > 0 ? "CREDIT" : "DEBIT";
      const suggestedAmount = Math.abs(totals.difference);
      setDialogDefaults({
        type: suggestedType,
        amount: suggestedAmount,
      });
    } else {
      setDialogDefaults(null);
    }

    setDialogOpen(true);
  };

  const handleOpenEditDialog = (index) => {
    const row = transactions[index];
    setEditIndex(index);
    setDialogDefaults({
      outletChartOfAccount: row.outletChartOfAccount,
      type: row.debit > 0 ? "DEBIT" : "CREDIT",
      amount: row.debit > 0 ? row.debit : row.credit,
      note: row.note || "",
      entityType: row.entityType || null,
      entity: row.entity || null,
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditIndex(null);
    setDialogDefaults(null);
  };

  const handleDialogSave = (data) => {
    if (editIndex !== null) {
      update(editIndex, data);
    } else {
      append(data);
    }
  };

  const processRowUpdate = useCallback(
    (newRow, oldRow) => {
      const index = newRow.index;
      const current = transactions[index];

      // Enforce mutual exclusivity: if debit changed and > 0, clear credit; vice versa
      let debit = Number(newRow.debit) || 0;
      let credit = Number(newRow.credit) || 0;
      const note = newRow.note || "";

      if (debit !== (Number(oldRow.debit) || 0) && debit > 0) {
        credit = 0;
      } else if (credit !== (Number(oldRow.credit) || 0) && credit > 0) {
        debit = 0;
      }

      update(index, { ...current, debit, credit, note });

      return { ...newRow, debit, credit, note };
    },
    [transactions, update],
  );

  const rows = useMemo(
    () =>
      fields.map((field, index) => ({
        id: field.id,
        index,
        ...(transactions?.[index] || {}),
      })),
    [fields, transactions],
  );

  const columns = useMemo(
    () => [
      {
        field: "outletChartOfAccount",
        headerName: "Akun",
        flex: 3,
        sortable: false,
        renderCell: ({ row }) => {
          const entityLabel = getEntityLabel(row);
          return (
            <Stack
              sx={{
                justifyContent: "center",
                py: 1
              }}>
              <Typography variant="body2">
                {row.outletChartOfAccount?.code} -{" "}
                {row.outletChartOfAccount?.name}
              </Typography>
              {entityLabel && (
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {entityLabel}
                </Typography>
              )}
            </Stack>
          );
        },
      },
      {
        field: "debit",
        headerName: "Debit",
        flex: 1,
        sortable: false,
        editable: true,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "credit",
        headerName: "Kredit",
        flex: 1,
        sortable: false,
        editable: true,
        type: "number",
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "note",
        headerName: "Catatan",
        flex: 1.5,
        sortable: false,
        editable: true,
      },
      {
        field: "actions",
        headerName: "Aksi",
        width: 100,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: ({ row }) => (
          <Stack direction="row" sx={{
            justifyContent: "center"
          }}>
            <IconButton
              size="small"
              onClick={() => handleOpenEditDialog(row.index)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => remove(row.index)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [transactions],
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center"
          }}>
          <Typography variant="h6">Transaksi Akun</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Tambah
          </Button>
        </Stack>

        {errors.outletChartOfAccountTransactions?.message && (
          <Alert severity="error">
            {errors.outletChartOfAccountTransactions.message}
          </Alert>
        )}

        <DataGrid
          rows={rows}
          columns={columns}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          disableColumnMenu
          processRowUpdate={processRowUpdate}
          localeText={{
            noRowsLabel:
              'Belum ada transaksi akun. Klik "Tambah" untuk menambahkan.',
          }}
          slots={{
            footer: () => (
              <Stack
                direction="row"
                sx={{
                  px: 1.5,
                  py: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: "bold",
                    flex: 3
                  }}>
                  Total
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={isBalanced ? "success.main" : "text.primary"}
                  sx={{
                    fontWeight: "bold",
                    flex: 1,
                    textAlign: "right"
                  }}>
                  {formatNumber(totals.debit)}
                </Typography>
                <Typography
                  variant="subtitle2"
                  color={isBalanced ? "success.main" : "text.primary"}
                  sx={{
                    fontWeight: "bold",
                    flex: 1,
                    textAlign: "right"
                  }}>
                  {formatNumber(totals.credit)}
                </Typography>
                <Stack
                  direction="row"
                  sx={{
                    alignItems: "center",
                    flex: 1.5
                  }}>
                  {!isBalanced && (
                    <Typography variant="caption" color="error" sx={{ ml: 2 }}>
                      Selisih: {formatNumber(Math.abs(totals.difference))}
                    </Typography>
                  )}
                  {isBalanced && totals.debit > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "success.main",
                        ml: 2
                      }}>
                      ✓ Seimbang
                    </Typography>
                  )}
                </Stack>
                <Stack sx={{ width: 100 }} />
              </Stack>
            ),
          }}
        />
      </Stack>
      {dialogOpen && (
        <AccountTransactionFormDialog
          onClose={handleDialogClose}
          onSave={handleDialogSave}
          outlet={outlet}
          defaultValues={dialogDefaults}
        />
      )}
    </Paper>
  );
};
